/**
 * Remote Kokoro via laptop server over Tailscale. No on-device model, no
 * browser memory pressure - the laptop runs full-precision Kokoro and
 * streams WAV back. Phone's only job is decoding and playing.
 *
 * Server: D:/Projects/doomscroll-tts-space/app.py (FastAPI + kokoro 0.9.4)
 * Exposed at https://shaunsmacbook.tail3e3e16.ts.net:8444/tts via `tailscale serve`.
 */

import type { LoadCallback } from './kokoro-ondevice';

const DEFAULT_SERVER_URL = 'https://shaunsmacbook.tail3e3e16.ts.net:8444';

export function getServerUrl(): string {
  return (localStorage.getItem('kokoroServerUrl') || DEFAULT_SERVER_URL).replace(/\/+$/, '');
}

const VOICE_MAP: Record<string, string> = {
  bella: 'af_bella', michael: 'am_michael', nicole: 'af_nicole',
  sarah: 'af_sarah', adam: 'am_adam', emma: 'bf_emma',
};

export type ServerSpeakOpts = {
  voice: string;
  speed?: number;
  audioCtx: AudioContext;
  onLoad?: LoadCallback;
  onSentence?: (idx: number, total: number) => void;
  onEnd?: () => void;
  isActive: () => boolean;
  /** When set, we check the offline audio cache (keyed by bookId+chapter+
   *  sentence+voice) before falling back to the network. Enables pinned
   *  books to play with the laptop off. */
  bookId?: string;
  chapterIdx?: number;
  sentenceStartIdx?: number;
};

// Module-level pointers so stopKokoro() can hard-abort in-flight work:
// cancel pending fetches, mute the run's gain node (kills currently-playing
// AND any sources scheduled for the future that haven't started yet - stop()
// alone doesn't reliably cancel future-scheduled nodes on iOS Safari).
let activeController: AbortController | null = null;
let activeGain: GainNode | null = null;

export function abortServerPlayback() {
  if (activeController) { try { activeController.abort(); } catch {} activeController = null; }
  if (activeGain) {
    try {
      const ctx = activeGain.context;
      activeGain.gain.cancelScheduledValues(ctx.currentTime);
      activeGain.gain.setValueAtTime(0, ctx.currentTime);
    } catch {}
    try { activeGain.disconnect(); } catch {}
    activeGain = null;
  }
}

async function fetchSentenceAudio(text: string, voice: string, speed: number, signal: AbortSignal): Promise<ArrayBuffer> {
  const url = getServerUrl() + '/tts';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, voice, speed }),
    signal,
  });
  if (!res.ok) throw new Error(`TTS server ${res.status}: ${await res.text().catch(() => '')}`);
  return await res.arrayBuffer();
}

type BatchPayload = { buf: ArrayBuffer; durations: number[] };

async function fetchBatchAudio(sentences: string[], voice: string, speed: number, signal: AbortSignal): Promise<BatchPayload> {
  const url = getServerUrl() + '/tts_batch';
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sentences, voice, speed }),
    signal,
  });
  if (!res.ok) throw new Error(`TTS batch ${res.status}: ${await res.text().catch(() => '')}`);
  const durHeader = res.headers.get('X-Sentence-Durations') || '';
  const durations = durHeader ? durHeader.split(',').map((s) => parseFloat(s) || 0) : sentences.map(() => 0);
  const buf = await res.arrayBuffer();
  return { buf, durations };
}

const LOOKAHEAD = 3;

export async function speakViaServer(sentences: string[], opts: ServerSpeakOpts): Promise<void> {
  const dlog = (m: string) => { try { (window as any).__doomLog?.('[server] ' + m); } catch {} };
  const ctx = opts.audioCtx;
  const voiceId = VOICE_MAP[opts.voice] || (opts.voice.includes('_') ? opts.voice : 'af_bella');
  const speed = opts.speed ?? 1;

  abortServerPlayback();
  const controller = new AbortController();
  activeController = controller;
  const signal = controller.signal;

  // Per-run gain node. All sources connect here instead of ctx.destination;
  // on abort we set gain to 0 AND disconnect - instantly silences everything
  // including scheduled future sources.
  const runGain = ctx.createGain();
  runGain.gain.value = 1;
  runGain.connect(ctx.destination);
  activeGain = runGain;

  opts.onLoad?.({ status: 'init', pct: 5, detail: 'Contacting laptop Kokoro...' });
  dlog('ctx sr=' + ctx.sampleRate + ' sentences=' + sentences.length + ' lookahead=' + LOOKAHEAD);

  const fetchAndDecode = async (text: string, globalIdx: number): Promise<AudioBuffer | null> => {
    try {
      // Cache hit path: if the book is pinned, audio is pre-rendered in
      // IndexedDB at speed=1.0. Read blob, decode, skip network.
      if (opts.bookId && typeof opts.chapterIdx === 'number') {
        const sentIdx = globalIdx + (opts.sentenceStartIdx ?? 0);
        const { getCachedAudio } = await import('./offline-audio');
        const cached = await getCachedAudio(opts.bookId, opts.chapterIdx, sentIdx, voiceId);
        if (cached) {
          const buf = await cached.arrayBuffer();
          if (signal.aborted) return null;
          return await ctx.decodeAudioData(buf);
        }
      }
      const buf = await fetchSentenceAudio(text, voiceId, speed, signal);
      if (signal.aborted) return null;
      return await ctx.decodeAudioData(buf.slice(0));
    } catch (e: any) {
      if (signal.aborted || e?.name === 'AbortError') return null;
      dlog('fetchAndDecode err ' + (e?.message || e));
      return null;
    }
  };

  // Prime LOOKAHEAD concurrent fetches. Server parallelizes - measured 3x
  // concurrent /tts completes in ~4s (vs 3s serial) so the prefetch window
  // gives us steady-state headroom even for short sentences.
  const pipeline: (Promise<AudioBuffer | null> | null)[] = [];
  for (let i = 0; i < Math.min(LOOKAHEAD, sentences.length); i++) pipeline.push(fetchAndDecode(sentences[i], i));

  // Scheduled start: instead of waiting for src.onended (which fires ~50ms
  // late on iOS Safari), we schedule each source to begin precisely when
  // the previous one is due to end. Browser audio engine stitches seamlessly.
  // Fallback: if the next buffer isn't ready by the scheduled time, we just
  // use src.start(0) on the next iteration (adds the normal fetch-bound gap
  // but doesn't break anything).
  let scheduledEndAt = 0;

  for (let i = 0; i < sentences.length; i++) {
    if (!opts.isActive() || signal.aborted) { dlog('stopped at ' + i); return; }
    opts.onSentence?.(i, sentences.length);

    const audioBuf = await pipeline.shift();
    if (i + LOOKAHEAD < sentences.length) pipeline.push(fetchAndDecode(sentences[i + LOOKAHEAD], i + LOOKAHEAD));

    if (!audioBuf) continue;
    if (!opts.isActive() || signal.aborted) return;

    if (i === 0) {
      dlog('first ready sr=' + audioBuf.sampleRate + ' dur=' + audioBuf.duration.toFixed(2) + 's');
      opts.onLoad?.({ status: 'ready', pct: 100 });
    }

    const now = ctx.currentTime;
    // First sentence: small ramp to now+0.05 to avoid click.
    // Later sentences: schedule at previous end IF it's still in the future,
    // else start immediately (we're behind on fetch - unavoidable gap).
    const startAt = i === 0 ? now + 0.05 : (scheduledEndAt > now ? scheduledEndAt : now);
    scheduledEndAt = startAt + audioBuf.duration;

    const src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(runGain);
    try { src.start(startAt); } catch (e: any) { dlog('start err ' + (e?.message || e)); continue; }

    // Wait until ~30ms before scheduled end, then loop to schedule the
    // next source. Leaves a small cushion for start() to be registered
    // before the current source ends.
    const waitMs = Math.max(0, (scheduledEndAt - now) * 1000 - 30);
    await new Promise<void>((resolve) => {
      let settled = false;
      const done = () => { if (!settled) { settled = true; resolve(); } };
      const t = window.setTimeout(done, waitMs);
      const onAbort = () => { clearTimeout(t); done(); };
      signal.addEventListener('abort', onAbort, { once: true });
      src.onended = () => { clearTimeout(t); done(); };
    });
  }

  if (!signal.aborted) {
    // Let the final scheduled source actually finish before firing onEnd.
    const tailMs = Math.max(0, (scheduledEndAt - ctx.currentTime) * 1000 + 50);
    await new Promise<void>((r) => setTimeout(r, tailMs));
  }
  try { runGain.disconnect(); } catch {}
  if (activeGain === runGain) activeGain = null;
  if (opts.isActive() && !signal.aborted) opts.onEnd?.();
  if (activeController === controller) activeController = null;
}

/** Probe the server - used on app start to decide which engine to prefer. */
export async function serverHealth(timeoutMs = 2500): Promise<{ ok: boolean; detail?: string }> {
  const ac = new AbortController();
  const t = setTimeout(() => ac.abort(), timeoutMs);
  try {
    const res = await fetch(getServerUrl() + '/', { signal: ac.signal });
    clearTimeout(t);
    if (!res.ok) return { ok: false, detail: 'status ' + res.status };
    const j: any = await res.json();
    return { ok: !!j?.ok, detail: j?.name };
  } catch (e: any) {
    clearTimeout(t);
    return { ok: false, detail: String(e?.message || e) };
  }
}
